import { isEqual } from 'lodash';
import { useCallback, useContext, useState } from 'react';
import {
  DispatchContext,
  StateContext,
} from '../contexts/use-menu-editor-context';
import { populateItemID } from '../utils';
import { MultiChildError } from '../utils/errors';
import { checkForResponseError, generateResponsePayload } from '../utils/http';
import updateCheckbox from '../utils/update-checkbox';

const generateItem = (item, type, parentId, position) => {
  return {
    ...item,
    type,
    position,
    parent_id: parentId,
  };
};

const generateChildren = item => {
  return (item.children || []).map((child, idx) => {
    const generatedChild = generateItem(child, child.type, item.id, idx);
    const generatedGrandchildren = generateChildren(generatedChild);

    return checkCostPrice(
      checkPrice(
        checkParent(
          { ...generatedChild, children: generatedGrandchildren },
          child.type
        )
      )
    );
  });
};

// Ensure a top level menu item doesn't have a parent ID set
function checkParent(newItem, type) {
  if (type === 'menu' || newItem.parent_id === '') {
    delete newItem['parent_id'];
  }

  return newItem;
}

// Enforce price field to be a Number
const checkPrice = i => (i.price ? { ...i, price: Number(i.price) } : i);

// Enforce cost_price field to be a Number
const checkCostPrice = i =>
  i.cost_price ? { ...i, cost_price: Number(i.cost_price) } : i;

export function identifyChildModifications(oldChildren = [], newChildren = []) {
  let childMods = [];
  let newChildIds = [];

  for (const newChild of newChildren || []) {
    const oldChild = (oldChildren || []).find(c => c.id === newChild.id);

    let grandchildMods = [];

    // Defaults to {} as there may not be an old child, say for example if creating a new child
    let { children: oldGrandchildren, ...oldChildWithoutChildren } =
      oldChild || {};
    let { children: newGrandchildren, ...newChildWithoutChildren } = newChild;
    oldGrandchildren = oldGrandchildren || [];
    newGrandchildren = newGrandchildren || [];

    // Base case, recursively checks for modifications to children
    if (oldGrandchildren.length > 0 || newGrandchildren.length > 0) {
      grandchildMods = identifyChildModifications(
        oldGrandchildren,
        newGrandchildren
      );
    }

    const mod = { menu: newChild, children: grandchildMods };

    newChildIds.push(newChild.id);

    if (!oldChild) {
      childMods.push({ ...mod, state: 'add' });

      continue;
    }

    const childHasChanged = !isEqual(
      oldChildWithoutChildren,
      newChildWithoutChildren
    );

    if (childHasChanged) {
      childMods.push({ ...mod, state: 'update' });

      continue;
    }

    if (grandchildMods.length > 0) {
      childMods.push({ ...mod, state: 'update-children' });
    }
  }

  for (const oldChild of oldChildren || []) {
    if (newChildIds.includes(oldChild.id)) {
      continue;
    }

    childMods.push({ menu: oldChild, state: 'delete' });
  }

  return childMods;
}

async function syncChildModifications(client, childMods) {
  if (childMods.length === 0) {
    return childMods;
  }
  const response = await client.post('/menu-operations', childMods);
  checkForResponseError(response);
  return response.data;
}

// NOTE: validations only get processed for the top level child modifications,
// so there is no need for deeper handling of errors currently.
function checkChildModificationErrors(childMods) {
  let errors = {};

  const modsWithErrors = childMods.filter(m => m.error);
  if (modsWithErrors && modsWithErrors.length > 0) {
    for (const m of modsWithErrors) {
      errors[m.menu.id] = m.error;
    }

    throw new MultiChildError('invalid fields', errors);
  }
}

export function setChildModificationsParentId(mods, parentId) {
  return mods.map(m => ({
    ...m,
    menu: { ...m.menu, parent_id: parentId },
  }));
}

// Any children that have a matching child modification are updated to their
// new values. The child matches a child modification if the menu's id matches
// or in the case where a new child is added, the previous_id will be checked on the
// child modification.
export function applyChildModifications(children, childMods) {
  if (!children) {
    return children;
  }

  return children.map(child => {
    const mod = childMods.find(
      m => m.menu.id === child.id || m.previous_id === child.id
    );

    if (mod && mod.menu) {
      return {
        ...mod.menu,
        children: applyChildModifications(child.children, mod.children),
      };
    }

    return child;
  });
}

export default function useEditor({
  init = {
    status: 'draft',
  },
  parent,
  validation,
  type,
  path,
  setShowEditor,
}) {
  const { id: parentId, label: parentLabel } = parent || {};

  if (init) {
    init.label = init.label || parentLabel || 'general';
  }

  const dispatch = useContext(DispatchContext);
  const { getState, calculatePosition, client } = useContext(StateContext);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editableItem, setEditableItem] = useState(init);

  const onCheckboxesChange = key => (selected, isChecked) => {
    const toBeUpdated = editableItem[key];
    const updated = updateCheckbox(toBeUpdated, selected, isChecked);
    const updatedState = {
      ...editableItem,
      [key]: updated,
    };
    setEditableItem(updatedState);
  };

  const onFieldChange = key => e => {
    setEditableItem({
      ...editableItem,
      [key]: e.target.value,
    });
  };

  const [childEditorsState, setChildEditorsState] = useState(null);
  const [childErrors, setChildErrors] = useState({});

  const onChildEditorSetup = validator => {
    // NOTE: ensure childEditorsState is set at most once for each child editor,
    // otherwise there could be an infinite loop of child editor re-renders.
    if (!childEditorsState) {
      setChildEditorsState({ validator });
    }
  };

  const onAddChild = child => {
    const childWithId = populateItemID(child);
    const children = [...(editableItem.children || []), childWithId];

    setEditableItem({ ...editableItem, children });
  };

  const onChangeChild = child => {
    const children = editableItem.children.map(oldChild => {
      if (oldChild.id === child.id) {
        return child;
      }

      return oldChild;
    });

    setEditableItem({ ...editableItem, children });
  };

  const onRemoveChild = child => {
    setEditableItem({
      ...editableItem,
      children: editableItem.children.filter(c => c.id !== child.id),
    });
  };

  const onSuccess = () => {
    setLoading(false); // set loading to false before editor goes out of scope
    setShowEditor(false);
  };

  const onError = e => {
    if (e instanceof MultiChildError) {
      setChildErrors(e.errorMessages());
    }
    setError(e.message);
    setLoading(false);
  };

  const fetchItem = () => {
    const generatedItem = generateItem(
      editableItem,
      type,
      parentId,
      calculatePosition(path)
    );
    return checkCostPrice(checkPrice(checkParent(generatedItem, type)));
  };

  const fetchChildModifications = item => {
    if (!childEditorsState) {
      return [];
    }

    const oldItem = getState(path);
    const oldChildren = oldItem && oldItem.children ? oldItem.children : [];
    const newChildren = generateChildren(item);

    return identifyChildModifications(oldChildren, newChildren);
  };

  const validateChildModifications = async childModifications => {
    if (!childEditorsState || !childEditorsState.validator) {
      return;
    }

    const validated = await Promise.all(
      childModifications.map(async mod => {
        if (mod.state === 'delete') {
          return mod;
        }

        try {
          await childEditorsState.validator.validate(mod.menu);
        } catch (e) {
          return { ...mod, error: e };
        }

        return mod;
      })
    );

    checkChildModificationErrors(validated);
  };

  const submitHandler = useCallback(
    cb => {
      (async () => {
        try {
          setLoading(true);

          const item = fetchItem();

          await validation.validate(item);

          const { id: tempId, ...newItem } = item;
          const childMods = fetchChildModifications(newItem);

          await validateChildModifications(childMods);

          const response = await client.post('/create-menu', newItem);
          checkForResponseError(response);

          window.Intercom('trackEvent', 'created-menu');

          const responseItem = { ...item, id: response.data.id };

          // Use newly created responseItem id as child parent id
          const childModsWithParentId = setChildModificationsParentId(
            childMods,
            responseItem.id
          );
          const syncedChildMods = await syncChildModifications(
            client,
            childModsWithParentId
          );

          const updatedResponseItem = {
            ...responseItem,
            children: applyChildModifications(
              responseItem.children,
              syncedChildMods
            ),
          };

          const payload = generateResponsePayload(
            type,
            updatedResponseItem,
            path
          );

          // Set the panel to expanded for newly created menus/categories
          if (payload.menu || payload.category) {
            payload.expanded = true;
          }

          // Update the overall list with the new item
          dispatch({ type: `CREATE_${type.toUpperCase()}_RESPONSE`, payload });
          onSuccess();
          if (cb) {
            cb(response.data.id);
          }
        } catch (e) {
          onError(e);
        }
      })();
    },
    [editableItem, childEditorsState]
  );

  const updateHandler = useCallback(() => {
    (async () => {
      try {
        setLoading(true);

        const item = fetchItem();

        await validation.validate(item);

        const childMods = fetchChildModifications(item);
        await validateChildModifications(childMods);

        const response = await client.post('/update-menu', item);
        checkForResponseError(response);

        const syncedChildMods = await syncChildModifications(client, childMods);

        const updatedResponseItem = {
          ...item,
          children: applyChildModifications(item.children, syncedChildMods),
        };

        const payload = generateResponsePayload(
          type,
          updatedResponseItem,
          path
        );

        // Update the overall list with the updated item, this will replace the
        // entire product and any children
        dispatch({ type: `UPDATE_${type.toUpperCase()}_RESPONSE`, payload });
        onSuccess();
      } catch (e) {
        onError(e);
      }
    })();
  }, [editableItem, childEditorsState]);

  const deleteHandler = () => {
    (async () => {
      try {
        setLoading(true);

        const response = await client.post('/delete-menu', {
          id: editableItem.id,
        });
        checkForResponseError(response);

        const payload = generateResponsePayload(type, {}, path);

        // Removes item and editor from view if successful (no need for onSuccess)
        dispatch({ type: `DELETE_${type.toUpperCase()}_RESPONSE`, payload });
      } catch (e) {
        onError(e);
      }
    })();
  };

  const submitOgHandler = (productIds, cb) => {
    (async () => {
      try {
        setLoading(true);
        const response = await client.post('/create-option-group', {
          ...editableItem.children[0],
          productIds,
        });
        checkForResponseError(response);
        onSuccess();
        if (cb) {
          cb(response.data.id);
        }
      } catch (e) {
        console.log({ e });
        onError(e);
      }
    })();
  };

  const deleteOgHandler = () => {
    (async () => {
      try {
        setLoading(true);
        const response = await client.post('/delete-option-group', {
          id: editableItem.id,
        });
        checkForResponseError(response);
        onSuccess();
      } catch (e) {
        onError(e);
      }
    })();
  };
  const updateOgHandler = productIds => {
    (async () => {
      try {
        setLoading(true);
        const response = await client.post('/update-option-group', {
          ...editableItem,
          productIds,
        });
        checkForResponseError(response);
        onSuccess();
      } catch (e) {
        onError(e);
      }
    })();
  };

  return {
    error,
    childErrors,
    loading,
    item: editableItem,
    submitHandler,
    updateHandler,
    deleteHandler,
    onFieldChange,
    onCheckboxesChange,
    onChildEditorSetup,
    onAddChild,
    onChangeChild,
    onRemoveChild,
    submitOgHandler,
    updateOgHandler,
    deleteOgHandler,
  };
}



// WEBPACK FOOTER //
// ./src/components/Dashboard/Panels/MenuEditor/hooks/use-editor.jsx